import { t } from "../../i18n";
import { useEffect, useState } from "react";
import {
  Copy,
  FlaskConical,
  GitBranch,
  KeyRound,
  LogIn,
  Pencil,
  Save,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import type {
  GeneratedGitSshCredential,
  GitCredentialDefinition,
  GitRepositoryDefinition,
  Integration,
} from "@lumen/shared";
import { api } from "../../api";
import { completePersonalizationRequestFromLocation } from "../../personalization";

type Forge = {
  id: string;
  name: string;
  host: string;
  kind: string;
};

type DiscoveredRepository = {
  externalId: string;
  name: string;
  cloneUrl: string;
  defaultBranch: string | null;
};

export function RepositoriesPage() {
  const [credentials, setCredentials] = useState<GitCredentialDefinition[]>([]);
  const [repositories, setRepositories] = useState<GitRepositoryDefinition[]>(
    [],
  );
  const [forges, setForges] = useState<Forge[]>([]);
  const [gitIntegrations, setGitIntegrations] = useState<Integration[]>([]);
  const [discoveredRepositories, setDiscoveredRepositories] = useState<
    Record<string, DiscoveredRepository[]>
  >({});
  const [discoveringIntegrationId, setDiscoveringIntegrationId] = useState("");
  const [importingExternalId, setImportingExternalId] = useState("");
  const [credentialName, setCredentialName] = useState("");
  const [kind, setKind] =
    useState<GitCredentialDefinition["kind"]>("https_token");
  const [host, setHost] = useState("github.com");
  const [username, setUsername] = useState("");
  const [secret, setSecret] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [cloneUrl, setCloneUrl] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [preserveRepositoryCredential, setPreserveRepositoryCredential] =
    useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [generatedPublicKey, setGeneratedPublicKey] = useState("");
  const [editingCredentialId, setEditingCredentialId] = useState("");
  const [editingRepositoryId, setEditingRepositoryId] = useState("");
  const [forgeId, setForgeId] = useState("");
  const [createIntegrationId, setCreateIntegrationId] = useState("");
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createPrivate, setCreatePrivate] = useState(true);
  const [createAutoInit, setCreateAutoInit] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const load = async () => {
    const [nextCredentials, nextRepositories, nextIntegrations] =
      await Promise.all([
        api<GitCredentialDefinition[]>("/git-credentials"),
        api<GitRepositoryDefinition[]>("/git-repositories"),
        api<Integration[]>("/integrations").catch(() => []),
      ]);
    setCredentials(nextCredentials);
    setRepositories(nextRepositories);
    setGitIntegrations(
      nextIntegrations.filter(
        (item) =>
          item.enabled &&
          ["github", "gitlab", "codeberg", "gitea"].includes(item.kind),
      ),
    );
    setForges(await api<Forge[]>("/organization/git-forges").catch(() => []));
    const params = new URLSearchParams(window.location.search);
    const oauthCredential = params.get("git_credential");
    if (oauthCredential) setCredentialId(oauthCredential);
    const oauthForge = params.get("git_forge");
    if (oauthForge) setForgeId(oauthForge);
    if (params.get("git_oauth") === "connected") {
      setTestResult({
        ok: true,
        message: "Gitea OAuth connected. The new credential is selected.",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  };
  const connectForge = async (forge: Forge) => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ authorizationUrl: string }>(
        `/git-forges/${forge.id}/oauth/start`,
        {
          method: "POST",
          body: JSON.stringify({ returnTo: window.location.pathname }),
        },
      );
      window.location.assign(result.authorizationUrl);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not start Gitea OAuth",
      );
      setBusy(false);
    }
  };
  const createRepository = async () => {
    if (!createIntegrationId || !createName.trim()) return;
    setBusy(true);
    setError("");
    try {
      const saved = await api<GitRepositoryDefinition>(
        "/git-repositories/create",
        {
          method: "POST",
          body: JSON.stringify({
            integrationId: createIntegrationId,
            name: createName,
            description: createDescription,
            private: createPrivate,
            autoInit: createAutoInit,
          }),
        },
      );
      setTestResult({ ok: true, message: "Created " + saved.name + "." });
      setCreateName("");
      setCreateDescription("");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not create repository",
      );
    } finally {
      setBusy(false);
    }
  };
  const importRepositories = async (integration: Integration) => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ imported: number }>(
        "/git-repositories/import",
        {
          method: "POST",
          body: JSON.stringify({ integrationId: integration.id }),
        },
      );
      setTestResult({
        ok: true,
        message: `Imported ${result.imported} repositories from ${integration.name}.`,
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not import repositories",
      );
    } finally {
      setBusy(false);
    }
  };
  const discoverRepositories = async (integration: Integration) => {
    setDiscoveringIntegrationId(integration.id);
    setError("");
    try {
      const result = await api<{ repositories: DiscoveredRepository[] }>(
        `/git-repositories/import?integrationId=${encodeURIComponent(integration.id)}`,
      );
      setDiscoveredRepositories((current) => ({
        ...current,
        [integration.id]: result.repositories,
      }));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not discover repositories",
      );
    } finally {
      setDiscoveringIntegrationId("");
    }
  };
  const importRepository = async (
    integration: Integration,
    repository: DiscoveredRepository,
  ) => {
    setImportingExternalId(repository.externalId);
    setError("");
    try {
      await api<{ imported: number }>("/git-repositories/import", {
        method: "POST",
        body: JSON.stringify({
          integrationId: integration.id,
          externalId: repository.externalId,
        }),
      });
      setTestResult({ ok: true, message: `Added ${repository.name}.` });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not import repository",
      );
    } finally {
      setImportingExternalId("");
    }
  };
  useEffect(() => {
    void load().catch((reason) => setError(reason.message));
  }, []);
  const addCredential = async () => {
    setBusy(true);
    setError("");
    try {
      const value =
        kind === "ssh_key"
          ? { privateKey: secret }
          : kind === "https_token"
            ? { token: secret }
            : { password: secret };
      await api(
        editingCredentialId
          ? `/git-credentials/${editingCredentialId}`
          : "/git-credentials",
        {
          method: editingCredentialId ? "PATCH" : "POST",
          body: JSON.stringify({
            name: credentialName,
            kind,
            username: username || null,
            hostPattern: host,
            ...(secret ? { secret: value } : {}),
          }),
        },
      );
      setCredentialName("");
      setSecret("");
      setEditingCredentialId("");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save credential",
      );
    } finally {
      setBusy(false);
    }
  };
  const addRepository = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await api<GitRepositoryDefinition>(
        editingRepositoryId
          ? `/git-repositories/${editingRepositoryId}`
          : "/git-repositories",
        {
          method: editingRepositoryId ? "PATCH" : "POST",
          body: JSON.stringify({
            name,
            slug: slug || undefined,
            cloneUrl,
            transport: cloneUrl.startsWith("http") ? "https" : "ssh",
            ...(preserveRepositoryCredential
              ? {}
              : { credentialId: credentialId || null }),
            integrationId: forgeId || null,
            enabled: true,
          }),
        },
      );
      if (!editingRepositoryId)
        await completePersonalizationRequestFromLocation({
          resourceType: "repository",
          resourceId: saved.id,
        }).catch(() => undefined);
      setName("");
      setSlug("");
      setCloneUrl("");
      setCredentialId("");
      setPreserveRepositoryCredential(false);
      setForgeId("");
      setEditingRepositoryId("");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save repository",
      );
    } finally {
      setBusy(false);
    }
  };
  const generateCredential = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api<GeneratedGitSshCredential>(
        "/git-credentials/generate-ssh-key",
        {
          method: "POST",
          body: JSON.stringify({
            name: credentialName,
            hostPattern: host,
            username: username || null,
          }),
        },
      );
      setGeneratedPublicKey(result.publicKey);
      setCredentialName("");
      setSecret("");
      setKind("ssh_key");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not generate SSH key",
      );
    } finally {
      setBusy(false);
    }
  };
  const test = async (path: string) => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ ok: boolean; message: string }>(path, {
        method: "POST",
        body: "{}",
      });
      setTestResult(result);
      if (result.ok) {
        const match = /\/git-repositories\/([^/]+)\/test/.exec(path);
        if (match)
          await completePersonalizationRequestFromLocation({
            resourceType: "repository",
            resourceId: match[1],
          }).catch(() => undefined);
      }
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Test failed");
    } finally {
      setBusy(false);
    }
  };
  const regenerate = async (id: string) => {
    if (
      !window.confirm(
        "Generate a replacement key pair? The old private key will stop being available to Lumen.",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      const result = await api<GeneratedGitSshCredential>(
        `/git-credentials/${id}/regenerate-ssh-key`,
        { method: "POST", body: "{}" },
      );
      setGeneratedPublicKey(result.publicKey);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not regenerate key",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{t("Repositories")}</h1>
          <p className="mt-1 text-sm text-stone-500">
            {t(
              "Manage source repositories and reusable credentials independently from agent workspace templates.",
            )}
          </p>
        </div>
        {gitIntegrations.length > 0 && (
          <div className="panel mb-5 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <GitBranch size={18} className="text-lumen-600" />
              <div className="min-w-0 flex-1">
                <h2 className="font-bold">
                  {t("Import from Git integrations")}
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "Add existing repositories from a connected Git provider with\n                  one click.",
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {gitIntegrations.map((integration) => (
                  <div key={integration.id} className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="button-primary"
                      disabled={busy}
                      onClick={() => void importRepositories(integration)}
                    >
                      <GitBranch size={14} />
                      {t("Import from {{name}}", { name: integration.name })}
                    </button>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={busy || discoveringIntegrationId !== ""}
                      onClick={() => void discoverRepositories(integration)}
                    >
                      {discoveringIntegrationId === integration.id
                        ? t("Loading repositories…")
                        : t("Choose repositories")}
                    </button>
                    {discoveredRepositories[integration.id] && (
                      <div className="mt-2 w-full space-y-2">
                        {discoveredRepositories[integration.id].length === 0 ? (
                          <p className="text-sm text-stone-500">
                            {t("No repositories available.")}
                          </p>
                        ) : (
                          discoveredRepositories[integration.id].map(
                            (repository) => (
                              <div
                                key={repository.externalId}
                                className="flex items-center justify-between gap-3 rounded border border-stone-200 p-2 text-sm"
                              >
                                <span className="min-w-0 truncate">
                                  {repository.name}
                                </span>
                                <button
                                  type="button"
                                  className="button-secondary shrink-0"
                                  disabled={busy || importingExternalId !== ""}
                                  onClick={() =>
                                    void importRepository(
                                      integration,
                                      repository,
                                    )
                                  }
                                >
                                  {importingExternalId === repository.externalId
                                    ? t("Adding…")
                                    : t("Add")}
                                </button>
                              </div>
                            ),
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {gitIntegrations.length > 0 && (
          <div className="panel mb-5 p-5">
            <div className="flex items-center gap-2">
              <GitBranch size={18} className="text-lumen-600" />
              <h2 className="font-bold">{t("Create a repository")}</h2>
            </div>
            <p className="mt-1 text-sm text-stone-500">
              {t("Create a new repository in a connected Git provider.")}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <select
                className="field"
                value={createIntegrationId}
                onChange={(event) => setCreateIntegrationId(event.target.value)}
              >
                <option value="">{t("Choose a Git integration")}</option>
                {gitIntegrations.map((integration) => (
                  <option key={integration.id} value={integration.id}>
                    {integration.name}
                  </option>
                ))}
              </select>
              <input
                className="field"
                placeholder={t("Repository name")}
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
              />
              <input
                className="field sm:col-span-2"
                placeholder={t("Description (optional)")}
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createPrivate}
                  onChange={(event) => setCreatePrivate(event.target.checked)}
                />
                {t("Private repository")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createAutoInit}
                  onChange={(event) => setCreateAutoInit(event.target.checked)}
                />
                {t("Initialize with README")}
              </label>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                className="button-primary"
                disabled={busy || !createIntegrationId || !createName.trim()}
                onClick={() => void createRepository()}
              >
                <Save size={14} /> {t("Create repository")}
              </button>
            </div>
          </div>
        )}
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="panel p-5">
            <div className="flex items-center gap-2">
              <KeyRound size={18} className="text-lumen-600" />
              <h2 className="font-bold">{t("Credentials")}</h2>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input
                className="field"
                placeholder={t("Name")}
                value={credentialName}
                onChange={(event) => setCredentialName(event.target.value)}
              />
              <input
                className="field font-mono text-xs"
                placeholder={t("Slug (human-readable ID)")}
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
              />
              <select
                className="field"
                value={kind}
                onChange={(event) => setKind(event.target.value as typeof kind)}
              >
                <option value="https_token">{t("HTTPS token")}</option>
                <option value="username_password">
                  {t("Username/password")}
                </option>
                <option value="ssh_key">{t("SSH key")}</option>
              </select>
              <input
                className="field"
                placeholder={t("Host")}
                value={host}
                onChange={(event) => setHost(event.target.value)}
              />
              <input
                className="field"
                placeholder={t("Username (optional)")}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>
            {kind === "ssh_key" ? (
              <textarea
                className="field mt-3 min-h-32 font-mono text-xs"
                placeholder={
                  editingCredentialId
                    ? t("Leave blank to retain the current private key")
                    : t(
                        "Paste an existing OpenSSH private key, or generate a new pair below",
                      )
                }
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
            ) : (
              <input
                type="password"
                className="field mt-3"
                placeholder={
                  editingCredentialId
                    ? t("Leave blank to retain the current secret")
                    : kind === "https_token"
                      ? t("Token")
                      : "Password"
                }
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
            )}
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {editingCredentialId && (
                <button
                  className="button"
                  onClick={() => {
                    setEditingCredentialId("");
                    setCredentialName("");
                    setSecret("");
                  }}
                >
                  {t("Cancel")}
                </button>
              )}
              {kind === "ssh_key" && !editingCredentialId && (
                <button
                  className="button"
                  disabled={busy || !credentialName || !host}
                  onClick={() => void generateCredential()}
                >
                  <Sparkles size={14} /> {t("Generate Ed25519 pair")}
                </button>
              )}
              <button
                className="button-primary"
                disabled={
                  busy ||
                  !credentialName ||
                  !host ||
                  (!editingCredentialId && !secret)
                }
                onClick={() => void addCredential()}
              >
                <Save size={14} />{" "}
                {editingCredentialId ? "Save changes" : "Add credential"}
              </button>
            </div>
            <div className="mt-5 divide-y divide-stone-100 dark:divide-white/10">
              {credentials.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap items-center gap-2 py-3 text-sm"
                >
                  <KeyRound size={14} />
                  <strong>{item.name}</strong>
                  <span className="text-xs text-stone-400">
                    {item.hostPattern}
                  </span>
                  <div className="ml-auto flex gap-1">
                    <button
                      className="button p-1.5"
                      title={t("Test credential")}
                      onClick={() =>
                        void test(`/git-credentials/${item.id}/test`)
                      }
                    >
                      <FlaskConical size={13} />
                    </button>
                    {item.kind === "ssh_key" && (
                      <button
                        className="button p-1.5"
                        title={t("Regenerate SSH key")}
                        onClick={() => void regenerate(item.id)}
                      >
                        <Sparkles size={13} />
                      </button>
                    )}
                    <button
                      className="button p-1.5"
                      title={t("Edit credential")}
                      onClick={() => {
                        setEditingCredentialId(item.id);
                        setCredentialName(item.name);
                        setKind(item.kind);
                        setHost(item.hostPattern);
                        setUsername(item.username ?? "");
                        setSecret("");
                      }}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="button p-1.5 text-red-600"
                      onClick={() =>
                        void api(`/git-credentials/${item.id}`, {
                          method: "DELETE",
                        }).then(load)
                      }
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="panel p-5">
            <div className="flex items-center gap-2">
              <GitBranch size={18} className="text-lumen-600" />
              <h2 className="font-bold">{t("Repositories")}</h2>
            </div>
            <div className="mt-4 space-y-3">
              <input
                className="field"
                placeholder={t("Name")}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <input
                className="field font-mono text-xs"
                placeholder={t("Git clone URL")}
                value={cloneUrl}
                onChange={(event) => setCloneUrl(event.target.value)}
              />
              {forges.length > 0 && (
                <select
                  className="field"
                  value={forgeId}
                  onChange={(event) => setForgeId(event.target.value)}
                >
                  <option value="">{t("Direct repository")}</option>
                  {forges.map((forge) => (
                    <option key={forge.id} value={forge.id}>
                      {forge.name} · {forge.host}
                    </option>
                  ))}
                </select>
              )}
              {forges.some((forge) => forge.kind === "gitea_oauth") && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
                  <span>{t("Per-user Gitea access:")}</span>
                  {forges
                    .filter((forge) => forge.kind === "gitea_oauth")
                    .map((forge) => (
                      <button
                        key={forge.id}
                        type="button"
                        className="button py-1.5 text-xs"
                        disabled={busy}
                        onClick={() => void connectForge(forge)}
                      >
                        <LogIn size={13} />
                        {t("Connect to {{name}}", { name: forge.name })}
                      </button>
                    ))}
                </div>
              )}
              <select
                className="field"
                value={credentialId}
                onChange={(event) => {
                  setCredentialId(event.target.value);
                  setPreserveRepositoryCredential(false);
                }}
              >
                <option value="">{t("No credential")}</option>
                {credentials.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              {editingRepositoryId && (
                <button
                  className="button"
                  onClick={() => {
                    setEditingRepositoryId("");
                    setName("");
                    setSlug("");
                    setCloneUrl("");
                    setCredentialId("");
                    setPreserveRepositoryCredential(false);
                    setForgeId("");
                  }}
                >
                  {t("Cancel")}
                </button>
              )}
              <button
                className="button-primary"
                disabled={busy || !name || !cloneUrl}
                onClick={() => void addRepository()}
              >
                <Save size={14} />{" "}
                {editingRepositoryId ? "Save changes" : "Add repository"}
              </button>
            </div>
            <div className="mt-5 divide-y divide-stone-100 dark:divide-white/10">
              {repositories.map((item) => (
                <div
                  key={item.id}
                  className="flex min-w-0 items-center gap-2 py-3 text-sm"
                >
                  <GitBranch size={14} />
                  <span className="min-w-0 flex-1">
                    <strong>{item.name}</strong>
                    <code className="ml-2 text-[11px] text-lumen-700 dark:text-lumen-300">
                      {item.slug}
                    </code>
                    <code className="block truncate text-[11px] text-stone-400">
                      {item.cloneUrl}
                    </code>
                    {item.defaultBranch && (
                      <span className="text-[11px] text-stone-400">
                        {t("Default branch: {{branch}}", {
                          branch: item.defaultBranch,
                        })}
                      </span>
                    )}
                  </span>
                  <button
                    className="button p-1.5"
                    title={t("Test repository access")}
                    onClick={() =>
                      void test(`/git-repositories/${item.id}/test`)
                    }
                  >
                    <FlaskConical size={13} />
                  </button>
                  <button
                    className="button p-1.5"
                    title={t("Edit repository")}
                    onClick={() => {
                      setEditingRepositoryId(item.id);
                      setName(item.name);
                      setSlug(item.slug);
                      setCloneUrl(item.cloneUrl);
                      setCredentialId(
                        item.credentialId === "[redacted]"
                          ? ""
                          : (item.credentialId ?? ""),
                      );
                      setPreserveRepositoryCredential(
                        item.credentialId === "[redacted]",
                      );
                      setForgeId(item.integrationId ?? "");
                    }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    className="button p-1.5 text-red-600"
                    onClick={() =>
                      void api(`/git-repositories/${item.id}`, {
                        method: "DELETE",
                      }).then(async () => {
                        await completePersonalizationRequestFromLocation({
                          resourceType: "repository",
                          resourceId: item.id,
                        }).catch(() => undefined);
                        await load();
                      })
                    }
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        {testResult && (
          <div
            className={`mt-5 flex items-center gap-3 rounded-xl border p-3 text-sm ${testResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/70 dark:bg-emerald-950/50 dark:text-emerald-200" : "border-red-200 bg-red-50 text-red-800 dark:border-red-800/70 dark:bg-red-950/50 dark:text-red-200"}`}
          >
            <FlaskConical size={16} />
            <span>{testResult.message}</span>
            <button
              className="ml-auto rounded-lg p-1.5 opacity-70 transition hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
              onClick={() => setTestResult(null)}
            >
              <X size={14} />
            </button>
          </div>
        )}
        {generatedPublicKey && (
          <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/60 dark:bg-emerald-950/20">
            <div className="flex items-start gap-3">
              <KeyRound
                className="mt-0.5 shrink-0 text-emerald-700"
                size={18}
              />
              <div className="min-w-0 flex-1">
                <h2 className="font-bold text-emerald-900 dark:text-emerald-200">
                  {t("SSH key generated")}
                </h2>
                <p className="mt-1 text-sm text-emerald-800/80 dark:text-emerald-300/80">
                  {t(
                    "The private key is encrypted and stored. Add this public key to your Git server now.",
                  )}
                </p>
                <pre className="mt-3 overflow-auto rounded-xl bg-white/70 p-3 font-mono text-xs break-all whitespace-pre-wrap dark:bg-black/20">
                  {generatedPublicKey}
                </pre>
                <button
                  className="button mt-3"
                  onClick={() =>
                    void navigator.clipboard.writeText(generatedPublicKey)
                  }
                >
                  <Copy size={14} /> {t("Copy public key")}
                </button>
              </div>
              <button
                className="button p-1.5"
                onClick={() => setGeneratedPublicKey("")}
              >
                <X size={15} />
              </button>
            </div>
          </div>
        )}
        {error && (
          <pre className="mt-5 rounded-xl bg-red-50 p-3 text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </pre>
        )}
      </div>
    </section>
  );
}
